// Copyright ©️ 2026 eVolve MEP, LLC

import './draft-table.css';

import React, { useEffect } from 'react';

import type { FacilityId, ShopConfiguration, WorkCell } from '@evolvemep/api-ts';
import { Button, Flex, Group } from '@mantine/core';
import { AgGridReact } from 'ag-grid-react';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridContainer } from 'helpers/ag-grid/AgGridContainer';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { NoRowsOverlay } from 'helpers/ag-grid/NoRowsOverlay';
import { useBlockerWhen } from 'hooks/navigation/useBlockerWhen';

import { ActiveWorkCellsGrid } from './ActiveWorkCellsGrid';
import { useWorkCellSetupModals } from './modalRegistries';
import { useWorkCellsGrid } from './useWorkCellsGrid';

type Props = {
  facilityId: FacilityId;
  /** The active, in use `shopConfiguration` */
  activeShopConfiguration: ShopConfiguration;
  draftShopConfiguration: ShopConfiguration;
};

export const DraftWorkCellSetup = ({ facilityId, activeShopConfiguration, draftShopConfiguration }: Props) => {
  const { openModal } = useWorkCellSetupModals();

  const { registerMessage } = useBlockerWhen('work-cell-setup', true);
  useEffect(
    () =>
      registerMessage(
        'Navigating away will remember your changes in edit mode, but will not reflect in the existing work cell setup.',
      ),
    [registerMessage],
  );

  const {
    agGridProps: draftAgGridProps,
    gridApi: draftGridApi,
    sorting: draftSorting,
    expandDetailGrid: expandDraftDetailGrid,
    refreshDetailGrid: refreshDraftDetailGrid,
  } = useWorkCellsGrid(facilityId, 'draft', draftShopConfiguration, true);

  return (
    <>
      <Group gap="sm" justify="flex-end" mb="xs">
        <Button
          leftSection={<EvolveIcon icon="Pin" size="sm" />}
          variant="light"
          onClick={() =>
            openModal('assignTasksPickerModal', {
              draftShopConfiguration,
              onWorkCellCreated: () => refreshGrid(draftGridApi),
              onTaskTypesAssigned: ({ workCellId }) => {
                expandDraftDetailGrid(workCellId, 'Tasks');
                refreshDraftDetailGrid(workCellId, 'Tasks');
              },
            })
          }
        >
          Assign tasks
        </Button>
        <Button
          onClick={() =>
            openModal('createEditWorkCellModal', {
              shopConfiguration: draftShopConfiguration,
              onWorkCellSaved: () => {
                refreshGrid(draftGridApi);
              },
            })
          }
          leftSection={<EvolveIcon icon="Add" size="sm" />}
          variant="light"
        >
          New work cell
        </Button>
      </Group>
      <Flex style={{ height: '100%' }} gap="sm">
        <ActiveWorkCellsGrid facilityId={facilityId} activeShopConfiguration={activeShopConfiguration} draftExists />
        <AgGridContainer w="50%" className="draft-table">
          {(applyWrap) => (
            <AgGridReact<WorkCell>
              loading={draftSorting}
              {...draftAgGridProps}
              onFirstDataRendered={applyWrap}
              noRowsOverlayComponent={() => <NoRowsOverlay label="Add a work cell." gridApi={draftGridApi} />}
            />
          )}
        </AgGridContainer>
      </Flex>
    </>
  );
};
